import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { BLUE, FONT, GOLD, WHITE } from "../theme";

export type PartnerMapMarker = {
  id: string;
  // Partner name shown as the marker heading.
  partnerName: string;
  // Branch website title (or address) shown under the partner name.
  title: string;
  // [longitude, latitude] — matches the rest of the catalog's convention.
  coordinates: [number, number];
};

type PartnerMapProps = {
  markers: PartnerMapMarker[];
  height?: number;
};

// Gold teardrop pin styled with the site theme, so we don't depend on Leaflet's
// default marker image assets (which break under CRA/webpack bundling).
const PIN_ICON = L.divIcon({
  className: "baldai-pin",
  html: `<div style="
    width:22px;height:22px;
    background:${GOLD};
    border:3px solid ${WHITE};
    border-radius:50% 50% 50% 0;
    transform:rotate(-45deg);
    box-shadow:0 2px 8px rgba(9,71,131,0.45);
  "></div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 22],
  popupAnchor: [0, -22],
  tooltipAnchor: [0, -16]
});

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function PartnerMap({ markers, height = 440 }: PartnerMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || markers.length === 0) {
      return;
    }

    const map = L.map(container, {
      scrollWheelZoom: false,
      attributionControl: true
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    }).addTo(map);

    // Leaflet uses [lat, lng]; our data is [lng, lat].
    const latLngs: L.LatLngTuple[] = markers.map(({ coordinates: [lng, lat] }) => [lat, lng]);

    markers.forEach((marker, index) => {
      const popupHtml = `
        <div style="font-family:${FONT};min-width:140px">
          <div style="color:${BLUE};font-weight:700;font-size:14px;margin-bottom:2px">
            ${escapeHtml(marker.partnerName)}
          </div>
          ${
            marker.title && marker.title !== marker.partnerName
              ? `<div style="color:${GOLD};font-weight:600;font-size:12px">${escapeHtml(
                  marker.title
                )}</div>`
              : ""
          }
        </div>`;

      const tooltipText =
        marker.title && marker.title !== marker.partnerName
          ? `${marker.partnerName} — ${marker.title}`
          : marker.partnerName;

      L.marker(latLngs[index], { icon: PIN_ICON, title: tooltipText })
        .addTo(map)
        .bindTooltip(escapeHtml(tooltipText), {
          direction: "top",
          opacity: 0.95
        })
        .bindPopup(popupHtml);
    });

    if (latLngs.length === 1) {
      map.setView(latLngs[0], 15);
    } else {
      map.fitBounds(L.latLngBounds(latLngs), { padding: [48, 48], maxZoom: 16 });
    }

    return () => {
      map.remove();
    };
  }, [markers]);

  return <div ref={containerRef} style={{ width: "100%", height, minHeight: height }} />;
}
